import { useEffect, type RefObject } from 'react';
import type { PipingSystem } from '../types';
import { useExportAvailableParameters } from './useExportAvailableParameters';

interface UseExportColumnParametersOptions {
  open: boolean;
  useDrawer: boolean;
  tourOpen: boolean;
  systems: PipingSystem[];
  activePanelRef: RefObject<HTMLDivElement | null>;
  activeSearchRef: RefObject<HTMLInputElement | null>;
  inlinePanelRef: RefObject<HTMLDivElement | null>;
  drawerPanelRef: RefObject<HTMLDivElement | null>;
  inlineSearchRef: RefObject<HTMLInputElement | null>;
  drawerSearchRef: RefObject<HTMLInputElement | null>;
}

export function useExportColumnParameters(options: UseExportColumnParametersOptions) {
  const availableParameters = useExportAvailableParameters({
    open: options.open,
    systems: options.systems,
  });

  useEffect(() => {
    options.activePanelRef.current = options.useDrawer
      ? options.drawerPanelRef.current
      : options.inlinePanelRef.current;
    options.activeSearchRef.current = options.useDrawer
      ? options.drawerSearchRef.current
      : options.inlineSearchRef.current;
  });

  useEffect(() => {
    if (!options.open) {
      return;
    }

    const focusHandle = options.tourOpen
      ? null
      : window.requestAnimationFrame(() => {
          const search = options.useDrawer
            ? options.drawerSearchRef.current
            : options.inlineSearchRef.current;
          search?.focus();
        });
    return () => {
      if (focusHandle !== null) {
        window.cancelAnimationFrame(focusHandle);
      }
    };
  }, [
    options.drawerSearchRef,
    options.inlineSearchRef,
    options.open,
    options.tourOpen,
    options.useDrawer,
  ]);

  return availableParameters;
}
